import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Box, Page, Spinner, TextButton } from '@zillow/constellation-10';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import { AdminNav, HeaderNavigation, MobileHeaderNav, MockUserNav, VerticalNavigation } from '..';
import TopNavBar from '../TopNavBar';

import { LEFT_NAV_WIDTH } from '../../constants';
import { getIaNavEnabled } from '../../utils/featureFlags';

import {
  appLayoutContainerRecipe,
  bodyContainerRecipe,
  footerContainerStyles,
  getFixedChromeTopOffset,
  layoutContainerRecipe,
  verticalNavigationContainerRecipe,
} from './styles';

import type React from 'react';
import type { User } from '../../types';
import type { Props as HeaderNavigationProps } from '../HeaderNavigation';

const SIDE_NAV_DENY_LIST = [
  // will match any string that starts with /rental-manager and optionally ends with a trailing slash
  /^\/rental-manager(?:\/$)?$/,
  // will match any string that starts with "/rental-manager/leasing/colandlord" and optionally ends with a trailing slash, and then any characters
  /^\/rental-manager\/leasing\/colandlord(?:\/$)?.*$/,
  // will match any string that starts with "/rental-manager/property-condition/colandlord" and optionally ends with a trailing slash, and then any characters
  /^\/rental-manager\/property-condition\/colandlord(?:\/$)?.*$/,
  // will match any string that starts with "/renter-hub/property-condition" and optionally ends with a trailing slash, and then any characters
  /^\/renter-hub\/property-condition(?:\/$)?.*$/,
];

export type Props = HeaderNavigationProps & {
  // set the loading state of the container, will render a full-page spinner
  isLoading?: boolean;
  // hides all navigation components (header, side nav, and mobile nav)
  hideNavigation?: boolean;
  // when true, renders sidebar navigation; when false, renders top header navigation
  useLeftNav?: boolean;
  // the current relative path so the nav link can be highlighted
  pathname: string;
  // page content
  children: React.ReactNode;
  userModel: User;
  isInMobileWebView?: boolean;
  // hides the privacy policy footer (ex. in mobile webview or wizard views)
  hideFooter?: boolean;
  // SSR-resolved opt-in for the new IA nav. When provided (e.g. the
  // host reads the request cookie or experiment assignment on the server), it's
  // used directly so the server and first client render agree (no hydration
  // flash). When omitted, the module falls back to a post-mount cookie read.
  iaNavEnabled?: boolean;
};

const getShouldUseLeftNav = (shouldBlockViaDenyList: boolean, useLeftNav: boolean) => {
  return !shouldBlockViaDenyList && useLeftNav;
};

const renderLoading = () => (
  <Page.Root data-testid="LoadingContainer" css={{ backgroundColor: 'bg.neutral', height: '100%' }}>
    <Page.Content css={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size="lg" />
    </Page.Content>
  </Page.Root>
);

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const AppLayoutContainer = (props: Props) => {
  const {
    hideNavigation = false,
    useLeftNav = true,
    isLoading = false,
    pathname,
    children = null,
    userModel = {} as User,
    isInMobileWebView = false,
    hideFooter = false,
    brand,
    useRelativePaths,
    host,
    iaNavEnabled: iaNavEnabledProp,
  } = props;

  const blockPath = SIDE_NAV_DENY_LIST.some((pattern) => {
    if (typeof pattern === 'string') {
      // For exact paths, check for an exact match
      return pathname === pattern;
    } else {
      // For patterns, test the path against the regex
      return pattern.test(pathname);
    }
  });

  const shouldUseLeftNav = getShouldUseLeftNav(blockPath, useLeftNav);

  // opt-in to the new IA navigation (top bar + slimmed left nav).
  // Prefer the SSR-resolved prop (no hydration flash); otherwise fall back to a
  // post-mount read of the Datarouter frontend-config flag (rollout) OR the QA
  // cookie override. Read after mount (not during render) because neither is
  // available during SSR and an inline read would mismatch on hydration.
  const [resolvedIaNavEnabled, setResolvedIaNavEnabled] = useState(false);
  const [bannerOffset, setBannerOffset] = useState(0);
  const bannerOverlayRef = useRef<HTMLDivElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (iaNavEnabledProp === undefined) {
      setResolvedIaNavEnabled(getIaNavEnabled());
    }
  }, [iaNavEnabledProp]);

  useIsomorphicLayoutEffect(() => {
    const bannerElement = bannerOverlayRef.current;

    if (!bannerElement || typeof window === 'undefined') {
      return;
    }

    const updateBannerOffset = () => {
      setBannerOffset(bannerElement.getBoundingClientRect().height);
    };

    updateBannerOffset();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateBannerOffset);

      return () => {
        window.removeEventListener('resize', updateBannerOffset);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      updateBannerOffset();
    });

    resizeObserver.observe(bannerElement);
    window.addEventListener('resize', updateBannerOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateBannerOffset);
    };
  }, []);

  const iaNavEnabled = iaNavEnabledProp ?? resolvedIaNavEnabled;

  const useAbsolutePaths = !useRelativePaths;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, host).getAllPaths();
  const { signInPage: signInPageFunc } = paths;

  const signInPage = signInPageFunc(pathname) || '';

  const showIaTopBar = shouldUseLeftNav && iaNavEnabled && hideNavigation === false;
  const showLeftNav = shouldUseLeftNav && hideNavigation === false;
  const fixedChromeTopOffset = getFixedChromeTopOffset({ bannerOffset, showIaTopBar });

  // Publish the IA offsets on :root so out-of-subtree elements can read them too: the portaled
  // renter-details drawer (renders at document.body) and the sibling app-download banner.
  // --ia-topnav-total-height (banner + bar + app banner) is the offset everything below the chrome
  // clears; --ia-topnav-bar-height (banner + bar, no app banner) is just for the app banner to pin
  // under the bar. The base (non-media) values use bannerOffset alone — the mock-user/admin banner
  // shows at every width, but the IA top bar (folded into fixedChromeTopOffset) is desktop-only.
  useEffect(() => {
    if (!showLeftNav && !showIaTopBar && bannerOffset === 0) return;

    const leftNavWidth = showLeftNav ? LEFT_NAV_WIDTH : 0;

    const style = document.createElement('style');
    // 80em is the Panda `xxl` breakpoint — keep in sync with the JSX overrides below.
    style.textContent = `:root{--ia-topnav-bar-height:${bannerOffset}px;--ia-topnav-total-height:calc(${bannerOffset}px + var(--app-banner-height, 0px));--ia-leftnav-width:0px}@media (min-width:80em){:root{--ia-topnav-bar-height:${fixedChromeTopOffset}px;--ia-topnav-total-height:calc(${fixedChromeTopOffset}px + var(--app-banner-height, 0px));--ia-leftnav-width:${leftNavWidth}px}}`;
    document.head.appendChild(style);

    return () => style.remove();
  }, [showLeftNav, showIaTopBar, fixedChromeTopOffset, bannerOffset]);

  const renderLayout = () => {
    return (
      <>
        {showIaTopBar && (
          <Box
            data-testid="TopNavBarContainer"
            css={{
              display: { base: 'none', xxl: 'block' },
              position: 'fixed',
              top: 'var(--mock-user-banner-offset)',
              left: 0,
              right: 0,
              zIndex: 'menu',
            }}
          >
            <TopNavBar {...props} />
          </Box>
        )}

        <Box
          data-testid={shouldUseLeftNav ? 'LeftNavLayoutContainer' : 'HeaderNavLayoutContainer'}
          css={{
            ...layoutContainerRecipe.raw({ isLeftNav: shouldUseLeftNav }),
            ...(hideNavigation
              ? {}
              : {
                  // Offset below the chrome (mock-user banner + IA top bar) and clamp height — else
                  // height:100% + marginTop overflows overflow:hidden and clips (e.g. SLW's inbox composer).
                  marginTop: { base: 'var(--mock-user-banner-offset)', xxl: 'var(--fixed-chrome-top-offset)' },
                  height: {
                    base: 'calc(100% - var(--mock-user-banner-offset))',
                    xxl: 'calc(100% - var(--fixed-chrome-top-offset))',
                  },
                  // Inline (not only :root) to avoid a first-render flash.
                  '--ia-topnav-total-height': {
                    base: 'var(--mock-user-banner-offset)',
                    xxl: 'var(--fixed-chrome-top-offset)',
                  },
                }),
          }}
        >
          {!isInMobileWebView && !hideNavigation ? (
            <Box css={{ display: { base: 'block', xxl: 'none' } }}>
              <MobileHeaderNav {...props} />
            </Box>
          ) : null}

          <Box css={{ display: { base: 'none', xxl: 'block' } }}>
            {shouldUseLeftNav ? (
              <>
                {hideNavigation === false && (
                  <Box
                    data-testid="LeftNavContainer"
                    css={{
                      ...verticalNavigationContainerRecipe.raw({ hideNavigation }),
                      top: 'var(--fixed-chrome-top-offset)',
                      height: 'calc(100% - var(--fixed-chrome-top-offset))',
                    }}
                  >
                    <VerticalNavigation displayMainMaintenanceTracker={false} {...props} topBar={iaNavEnabled} />
                  </Box>
                )}
              </>
            ) : (
              <>{hideNavigation === false && <HeaderNavigation {...props} />}</>
            )}
          </Box>

          <Box
            data-testid="BodyContainer"
            css={bodyContainerRecipe.raw({ hideNavigation, isLeftNav: shouldUseLeftNav })}
          >
            <Box data-testid="BodyContentContainer" css={{ width: '100%', minHeight: '100%' }}>
              {children}
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box
      data-testid="AppLayoutContainer"
      data-ia-nav={iaNavEnabled}
      css={appLayoutContainerRecipe.raw({ isLeftNav: shouldUseLeftNav })}
      style={
        {
          '--mock-user-banner-offset': `${bannerOffset}px`,
          '--fixed-chrome-top-offset': `${fixedChromeTopOffset}px`,
        } as React.CSSProperties
      }
    >
      <Box
        ref={bannerOverlayRef}
        data-testid="BannerOverlayContainer"
        css={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200000 }}
      >
        <MockUserNav brand={brand} featureFlagPage={paths.featureFlagPage} userModel={userModel} />
        <AdminNav brand={brand} paths={paths} signInPage={signInPage} userModel={userModel} pathname={pathname} />
      </Box>

      {blockPath ? renderLayout() : isLoading ? renderLoading() : renderLayout()}

      {!hideFooter && (
        <Page.Root css={footerContainerStyles.raw({ useLeftNav: shouldUseLeftNav })}>
          <Page.Content css={{ textAlign: 'end' }}>
            <TextButton textStyle="body-sm" title="Privacy Policy" tone="neutral" asChild>
              <a href="https://www.zillow.com/corporate/zg-privacy-notice/">Privacy Policy</a>
            </TextButton>
          </Page.Content>
        </Page.Root>
      )}
    </Box>
  );
};

export default AppLayoutContainer;
